import "../../datagrid.scss";
import { useContext, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ClientesContext } from '../../../context/clientes/ClientesContext';
import { Paginator } from '../../../components/paginator/Paginator';
import { headers } from "./configGrid/headers";
import { formatRows } from "./configGrid//rows";
import { indice } from "../../../components/DataGrid/indice";
import { getActionColumn } from "../../../components/ActionesColumns/ActionColumns";
import CircularProgress from "@mui/material/CircularProgress";
import useDocumentTitle from "../../../components/Title";
import CustomDataGrid from "../../../components/DataGrid/CustomDataGrid";
import ConfirmDialog from "../../../components/dialogs/DeleteDialog";
import ErrorDialog from "../../../components/dialogs/ErrorDeleteDialog";
import { fetchEntityData, deleteItem } from './configGrid/apiCalls';
import FiltersGrid from './filtersGrid/filtersGrid';
import { useFilters } from "../../../context/FiltersContext";
import { AccionesMasivasModal } from './ClientesAcciones';

export const Datatable = ({ onSelect, onClose, enableCreate = true }) => {
  const titleGrid = "Lista de Clientes";
  useDocumentTitle(titleGrid);
  const { updateFilters, getFilters } = useFilters();
  const contexto = useContext(ClientesContext);
  const {
    deleteClienteData,
    getClientesPanelData,
    permitirEdicionCompleta,
  } = contexto;
  const navigate = useNavigate();
  const nameData = "clientes";
  const descData = "cli_name";
  const getData = getClientesPanelData;
  const deleteData = deleteClienteData;

  // Estados básicos
  const [listaItems, setListaItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [numPagina, setNumPagina] = useState(1);
  const [filas, setFilas] = useState(isMobile ? 20 : 100);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchIDFilter, setSearchIDFilter] = useState(null);
  const [searchDescFilter, setSearchDescFilter] = useState(null);
  const searchInputRef = useRef(null);
  const isFetchingRef = useRef(false);

  // Estados para modales
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  // Estados para Acciones Masivas
  const [isAccionesModalOpen, setIsAccionesModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [isInitialized, setIsInitialized] = useState(false);
  const pageKey = "clientes";

  useEffect(() => {
    const savedFilters = getFilters(pageKey);
    if (savedFilters) {
      setSearchTerm(savedFilters.searchTerm || "");
      setSearchIDFilter(savedFilters.searchIDFilter || "");
      setSearchDescFilter(savedFilters.searchDescFilter || "");
      setNumPagina(1);
    }
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    fetchData();
    loadFilters();
  }, [isInitialized, searchTerm, searchIDFilter, numPagina, filas]);

  const loadFilters = () => {
    updateFilters(pageKey, { searchTerm, searchIDFilter, searchDescFilter });
  };

  const fetchData = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const { dataEntity, totalItems, totalPage } = await fetchEntityData(
        getData, searchTerm, searchIDFilter || "", numPagina, filas
      );
      if (numPagina > totalPage) {
        setNumPagina(totalPage);
      } else {
        setListaItems(dataEntity);
        setTotalItems(totalItems);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Handlers para eliminación
  // ═══════════════════════════════════════════════════════════════════════════
  const handleDelete = async () => {
    try {
      await deleteItem(deleteData, selectedItem);
      fetchData();
      closeDeleteModal();
    } catch (error) {
      setErrorMessage("No se pudo eliminar el elemento.");
      setIsErrorDialogOpen(true);
    }
  };

  const openDeleteModal = (id) => {
    setSelectedItem(id);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedItem(null);
    setIsDeleteModalOpen(false);
  };

  const closeErrorDialog = () => {
    setErrorMessage("");
    setIsErrorDialogOpen(false);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Handlers generales
  // ═══════════════════════════════════════════════════════════════════════════
  const handleClickEdit = (e) => {
    if (e) navigate(`/${nameData}/new/${e}`);
  };

  const handleSearch = () => {
    setSearchTerm(searchInputRef.current?.value || "");
  };

  const handleClear = () => {
    setSearchTerm("");
    if (searchInputRef.current) searchInputRef.current.value = "";
  };

  const handleRowClick = (params) => {
    onSelect && onSelect(params.row.id, params.row[descData]);
    onClose && onClose();
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Handler para Acciones Masivas
  // ═══════════════════════════════════════════════════════════════════════════
  const handleExecuteAccion = async (emp_id, option, info) => {
    setIsProcessing(true);

    try {
      const result = await permitirEdicionCompleta(emp_id, option);

      if (result?.success) {
        alert(`✅ ${info.accion.titulo}\n\nAplicado a: ${info.filtroTexto}\nRegistros afectados: ${result.affected || 0}`);
        fetchData();
        return true; // Indica éxito para cerrar el modal
      } else {
        setErrorMessage(result?.error || "Error al ejecutar la acción");
        setIsErrorDialogOpen(true);
        return false;
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("Error al ejecutar la acción");
      setIsErrorDialogOpen(true);
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const datosColum = useMemo(() => {
    const commonCols = indice(numPagina, filas).concat(headers.filter((c) => !c.hidden));
    return onSelect ? commonCols : commonCols.concat(getActionColumn(handleClickEdit, openDeleteModal));
  }, [onSelect, numPagina, filas]);

  const rows = useMemo(() => formatRows(listaItems), [listaItems]);

  return (
    <div className="datatable">
      <div className="datatableTitle">
        <h3>{titleGrid}</h3>

        <div className="linkContainer" style={{ display: 'flex', gap: '10px' }}>
          {enableCreate && (
            <Link to={`/${nameData}/new`} className="link">Agregar</Link>
          )}
          <button
            className="link"
            onClick={() => setIsAccionesModalOpen(true)}
            disabled={isProcessing}
            style={{
              background: isProcessing ? '#9ca3af' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              cursor: isProcessing ? 'wait' : 'pointer',
              border: 'none',
              color: 'white',
              padding: '5px 10px',
              borderRadius: '5px',
              fontSize: '14px',
            }}
          >
            {isProcessing ? '⏳ Procesando...' : '⚙️ Acciones Masivas'}
          </button>
        </div>

        <FiltersGrid
          searchInputRef={searchInputRef}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onSearch={handleSearch}
          onClose={handleClear}
          setSearchIDFilter={setSearchIDFilter}
          setSearchDescFilter={setSearchDescFilter}
          searchDescFilter={searchDescFilter}
        />
      </div>

      {isLoading ? (
        <div className="loading">
          <div style={{ display: "flex", justifyContent: "center", margin: "20px" }}>
            <h1>Cargando datos...</h1>
            <CircularProgress />
          </div>
        </div>
      ) : (
        <>
          <div className="datagridWrapper">
            <CustomDataGrid
              rows={rows}
              columns={datosColum}
              pageSize={filas}
              rowCount={totalItems}
              onRowClick={handleRowClick}
              onPageChange={setNumPagina}
              onPageSizeChange={(newPageSize) => { setFilas(newPageSize); setNumPagina(1); }}
              isLoading={isLoading}
            />
          </div>
          <div className="paginatorWrapper">
            <Paginator
              numPagina={numPagina}
              setNumPagina={setNumPagina}
              totalPages={Math.ceil(totalItems / filas)}
              filas={filas}
              setFilas={setFilas}
              totalDataItems={totalItems}
            />
          </div>
        </>
      )}

      {/* Modal de confirmación para eliminar */}
      <ConfirmDialog
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        nameData={nameData}
      />

      {/* Modal de error */}
      <ErrorDialog
        open={isErrorDialogOpen}
        onClose={closeErrorDialog}
        errormessages={errorMessage}
      />

      {/* Modal de Acciones Masivas */}
      <AccionesMasivasModal
        open={isAccionesModalOpen}
        onClose={() => setIsAccionesModalOpen(false)}
        onExecute={handleExecuteAccion}
        isProcessing={isProcessing}
      />
    </div>
  );
};

export default Datatable;
